import React, { useEffect, useState } from 'react';
import {
    rootActions,
    useAppDispatch,
    useAppSelector
} from '../../data/store';
import ProductPriceFormat from '../../../../products/product-price/product-price-format';
import Coupon from '../coupon/coupon';
import { CartSteps } from '../../enums/cart-steps';
import InstantSavings from '../instant-savings/instant-savings';
import StepOverviewItem from './step-overview-item';
import {
    ConfirmationMsgVisibleReferer,
    IConfirmationMsgVisibleResult,
    isConfirmationMsgVisible
} from '../../domain/free-plugin-provider';
import FreePluginBox from '../free-plugin/free-plugin-box';
import { sendGAEvent } from '../../../../../domain/marketing/gtm-provider';
import FreeWithYourPurchaseOverview from '../free-with-your-purchase/free-with-your-purchase-overview';
import SubscriptionOverviewItem from '../subscription/subscription-overview-item';
import ConfirmText from './confirm-text';
import ReferCredit from '../refer-credit/refer-credit';
import ReferCreditRecurring from '../refer-credit/credit-recurring';
import ConfirmOrderBtn from '../confirm-order/confirm-order-btn';
import { isEqual } from 'lodash';
import UpsellBox from '../../../../../domain/upsell/upsell-box/upsell-box';
import { UpsellReferrer } from '../../../../../domain/upsell/interfaces';
import FreePluginBoxMilestones from '../free-plugin/free-plugin-box-milestones/free-plugin-box-milestones';

const Step3Overview = () => {

    const dispatch = useAppDispatch();

    const cartData = useAppSelector(store => store.root.cartData, isEqual);
    const priceData = cartData.priceData;

    const [showAddMoreBox, setShowAddMoreBox] = useState(false);
    const [addMoreBox, setAddMoreBox] = useState<IConfirmationMsgVisibleResult|null>(null);

    useEffect(() => {
        if(!cartData) return;

        // https://wavesaudio.atlassian.net/browse/DSWEB-5070
        // https://wavesaudio.atlassian.net/browse/MRKT-27499
        const confirmationMessageVisibility = isConfirmationMsgVisible(
            cartData,
            ConfirmationMsgVisibleReferer.Cart,
            3,
        );

        const isVisible = confirmationMessageVisibility?.addMoreEnabled ||
            confirmationMessageVisibility?.progressBarEnabled;

        setShowAddMoreBox(isVisible);
        setAddMoreBox(confirmationMessageVisibility);
    }, [
        cartData,
    ]);

    /**
     * return to the first step
     */
    const editCartHandler = () => {
        dispatch(
            rootActions.main({
                cartStep: CartSteps.First,
            })
        );

        sendGAEvent('Edit Cart', 'Step 3');
    };


    return (
        <div className="bg-white border rounded w-full md:w-5/12 lg:w-1/4 md:top-60px sticky self-start">
            <div className="m-6">

                <div className="flex items-center justify-between font-bold mb-4">
                    <p className="text-2xl text-gray-700 mr-2">Order Summary</p>

                    <button
                        type="button"
                        onClick={ editCartHandler }
                        data-qa="cart-step3-edit-cart"
                        className="text-blue-200 font-bold text-right">
                        Edit Cart
                    </button>
                </div>

                {
                    cartData && cartData.cartItems && cartData.cartItems
                        .filter(cartItem => cartItem.isActive)
                        .map((cartItem, i) => {

                            return (
                                <StepOverviewItem
                                    key={ cartItem.cartItemGUID }
                                    cartItem={ cartItem }
                                />
                            )
                        })
                }

                <SubscriptionOverviewItem />
                { cartData.subscription && <div className="border-b mb-4"></div> }

                <FreeWithYourPurchaseOverview />

                {
                    priceData.subTotalPrice !== priceData.totalPrice &&
                    <div className="text-gray-500 font-bold flex items-center justify-between mb-2">
                        <span className="mr-2">Subtotal</span>
                        <span className="ml-2" data-qa="cart-step3-subtotal"><ProductPriceFormat price={ priceData.subTotalPrice } /></span>
                    </div>
                }

                {
                    priceData.shipping > 0 &&
                    <div className="text-gray-500 font-bold flex items-center justify-between mb-2">
                        <span className="mr-2">Shipping</span>
                        <span className="ml-2" data-qa="cart-step3-ship-value"><ProductPriceFormat price={ priceData.shipping } /></span>
                    </div>
                }

                {
                    priceData.taxWithoutShippingTax > 0 &&
                    <div className="text-gray-500 font-bold flex items-center justify-between mb-2">
                        <span className="mr-2">Tax</span>
                        <span className="ml-2" data-qa="cart-step3-tax-value"><ProductPriceFormat price={ priceData.taxWithoutShippingTax } /></span>
                    </div>
                }

                {
                    priceData.shippingTax > 0 &&
                    <div className="text-gray-500 font-bold flex items-center justify-between mb-2">
                        <span className="mr-2">Shipping Tax</span>
                        <span className="ml-2" data-qa="cart-step3-ship-tax-value"><ProductPriceFormat price={ priceData.shippingTax } /></span>
                    </div>
                }

                {
                    priceData.totalDiscountPrice > 0 &&
                    <div className="text-green font-bold flex items-center justify-between mb-4">
                        <span className="mr-2">Discounts</span>
                        <span className="ml-2" data-qa="cart-step3-discounts-price">-<ProductPriceFormat price={ priceData.totalDiscountPrice } /></span>
                    </div>
                }

                <div className="text-3xl text-gray-700 flex items-center justify-between mb-4">
                    <span className="mr-2">Order Total</span>
                    <span className="ml-2" data-qa="cart-step3-total"><ProductPriceFormat price={ priceData.totalPrice } /></span>
                </div>

                <Coupon />
                <InstantSavings />

                {
                    showAddMoreBox && !!addMoreBox &&
                    <FreePluginBox
                        visibility={ addMoreBox }
                        cartData={ cartData }
                        classes="mt-4"
                    />
                }

                <FreePluginBoxMilestones
                    cartData={ cartData }
                    classes="mt-4"
                    referrer={ ConfirmationMsgVisibleReferer.Cart }
                    cartStep={ 3 }
                />

                <UpsellBox
                    cartData={ cartData }
                    classes="mt-4"
                    referrer={ UpsellReferrer.CartStep3 }
                />

                <ReferCredit />
                <ReferCreditRecurring />

                <ConfirmText />
                <ConfirmOrderBtn />
            </div>
        </div>
    )
};

export default Step3Overview;
